import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { requestApiJson } from '../../api/client';
import '../../css/common/AppDownloadBanner.css';
import {
  ANDROID_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
  buildOpenInAppLaunchTarget,
  buildOpenInAppUrl,
  launchOpenInAppTarget,
  resolveOpenInAppPlatform,
} from '../../utils/openInAppLinks';

const APP_DOWNLOAD_LINKS = [
  {
    key: 'ios',
    platform: 'iOS',
    primaryLabel: 'for iOS',
    secondaryLabel: 'AppleStoreへ',
    href: IOS_APP_STORE_URL,
    iconClass: 'fa-brands fa-apple',
  },
  {
    key: 'android',
    platform: 'Android',
    primaryLabel: 'for Android',
    secondaryLabel: 'GooglePlayへ',
    href: ANDROID_PLAY_STORE_URL,
    iconClass: 'fa-brands fa-android',
  },
  {
    key: 'mac',
    platform: 'Mac',
    primaryLabel: 'for Mac',
    secondaryLabel: 'ダウンロード',
    href: '/api/mac-release/latest',
    iconClass: 'fa-solid fa-desktop',
  },
];

export const APP_DOWNLOAD_NOTICE_STORAGE_KEY = 'casticart:app-download-notice:v1:dismissed';
const MAC_RELEASE_STATUS_PATH = '/api/mac-release/latest.json';

const buildApiPath = (path) => {
  const apiOrigin = String(process.env.REACT_APP_API_URL || '')
    .replace(/\/api\/?$/i, '')
    .replace(/\/+$/, '');
  return apiOrigin ? `${apiOrigin}${path}` : path;
};

const AppDownloadBanner = ({
  className = '',
  dismissible = false,
  showTitle = true,
  storageKey = APP_DOWNLOAD_NOTICE_STORAGE_KEY,
}) => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [macReleaseAvailable, setMacReleaseAvailable] = useState(false);
  const publishedLinks = APP_DOWNLOAD_LINKS.filter(
    (item) => item.href && (item.key !== 'mac' || macReleaseAvailable)
  );
  const currentPlatform = useMemo(() => resolveOpenInAppPlatform(), []);
  const appLink = useMemo(
    () =>
      buildOpenInAppUrl({
        pathname: location.pathname,
        search: location.search,
      }),
    [location.pathname, location.search]
  );
  useEffect(() => {
    if (!dismissible || typeof window === 'undefined') return;
    setDismissed(window.localStorage.getItem(storageKey) === '1');
  }, [dismissible, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let cancelled = false;

    const loadMacReleaseAvailability = async () => {
      try {
        const { data, response } = await requestApiJson(MAC_RELEASE_STATUS_PATH, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller?.signal,
        });
        if (cancelled) return;
        if (!response.ok) {
          setMacReleaseAvailable(false);
          return;
        }
        setMacReleaseAvailable(data?.success === true);
      } catch (releaseError) {
        if (!cancelled && releaseError?.name !== 'AbortError') {
          setMacReleaseAvailable(false);
        }
      }
    };

    loadMacReleaseAvailability();
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, []);

  if (!publishedLinks.length || dismissed) return null;

  const resolveLink = (link) => {
    const launchTarget =
      currentPlatform === link.key && appLink
        ? buildOpenInAppLaunchTarget(appLink, link.key)
        : null;
    return {
      href: launchTarget?.href || (link.key === 'mac' ? buildApiPath(link.href) : link.href),
      launchTarget,
    };
  };

  const handleAppOpen = (event, launchTarget) => {
    if (!launchTarget?.href) return;
    event.preventDefault();
    launchOpenInAppTarget(launchTarget);
  };

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, '1');
    }
    setDismissed(true);
  };

  return (
    <section
      className={`app-download-banner${className ? ` ${className}` : ''}${dismissible ? ' app-download-banner--dismissible' : ''}${showTitle ? '' : ' app-download-banner--titleless'}`}
      aria-label="Casticartアプリのお知らせ"
    >
      {dismissible && (
        <button
          type="button"
          className="app-download-banner__close"
          aria-label="アプリ版のお知らせを閉じる"
          onClick={handleDismiss}
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      )}
      <div className="app-download-banner__mark" aria-hidden="true">
        <i className="fa-solid fa-layer-group" />
      </div>
      {showTitle ? (
        <div className="app-download-banner__body">
          <h2 className="app-download-banner__title">アプリ版を公開しました</h2>
        </div>
      ) : null}
      <div className="app-download-banner__actions">
        {publishedLinks.map((link) => {
          const resolved = resolveLink(link);
          return (
            <a
              key={link.key}
              className={`app-download-banner__button app-download-banner__button--${link.key}`}
              href={resolved.href}
              target={resolved.launchTarget ? undefined : '_blank'}
              rel={resolved.launchTarget ? undefined : 'noopener noreferrer'}
              aria-label={`${link.platform}アプリ ${link.secondaryLabel}`}
              onClick={(event) => handleAppOpen(event, resolved.launchTarget)}
            >
              <i className={link.iconClass} aria-hidden="true" />
              <span className="app-download-banner__buttonText">
                <span className="app-download-banner__buttonPrimary">{link.primaryLabel}</span>
                <span className="app-download-banner__buttonSecondary">{link.secondaryLabel}</span>
              </span>
            </a>
          );
        })}
      </div>
      <p className="app-download-banner__feature">アプリでもっと便利に！通知を見逃さない！</p>
    </section>
  );
};

export default AppDownloadBanner;
